import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { Link } from 'react-router';
import { Drawer, ErrorState, IconButton, Loading } from '@/shared/ui';
import { useGetCatalogQuery, useGetSubscriptionQuery } from '../api/portalApi';
import type { SubscriptionDetail } from '../api/types';
import { accountClosed, accountOpened, selectAccountDrawerId } from '../model/uiSlice';
import {
  MrrSpark,
  SubscriptionDetailsList,
  SubscriptionHistory,
  SubscriptionStats,
} from './SubscriptionSummary';
import { subscriptionDetails } from '../model/subscriptionDetails';

const BILLING_LABELS: Record<string, string> = {
  Yearly: 'Stripe · yearly',
  Monthly: 'Stripe · monthly',
  'Enterprise contract': 'Enterprise contract',
  Trial: 'Trial',
};

/**
 * Account drawer, mounted once in the shell. Any element with `data-account-id` opens it
 * (click, or Enter on a table row), as in the prototype.
 */
export function AccountDrawer() {
  const dispatch = useAppDispatch();
  const id = useAppSelector(selectAccountDrawerId);

  useEffect(() => {
    const open = (target: EventTarget | null) => {
      const trigger =
        target instanceof Element ? target.closest<HTMLElement>('[data-account-id]') : null;
      const accountId = trigger?.dataset.accountId;
      if (accountId) dispatch(accountOpened(accountId));
    };
    const onClick = (event: MouseEvent) => {
      open(event.target);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && event.target instanceof HTMLTableRowElement) open(event.target);
    };
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [dispatch]);

  return (
    <Drawer open={id !== null} onClose={() => dispatch(accountClosed())} labelledBy="drawer-name">
      {id && <DrawerContent id={id} onClose={() => dispatch(accountClosed())} />}
    </Drawer>
  );
}

function DrawerContent({ id, onClose }: { id: string; onClose: () => void }) {
  const { data: account, error, isLoading, refetch } = useGetSubscriptionQuery(id);
  const { data: catalog } = useGetCatalogQuery(undefined);
  const lifeLabel =
    catalog?.lifecycle.find((l) => l.id === account?.lifecycle)?.label ?? account?.lifecycle ?? '';

  return (
    <>
      <header className="drawer__head">
        <div className="drawer__titles">
          <span className="overline">Subscription</span>
          <h2 className="drawer__name" id="drawer-name">
            {account?.name ?? ''}
          </h2>
          <p className="drawer__tags">
            <span className="badge" data-life={account?.lifecycle}>
              {lifeLabel}
            </span>
            {account && <span>{account.offer}</span>}
            {account && <span>{BILLING_LABELS[account.billing] ?? account.billing}</span>}
          </p>
        </div>
        <IconButton label="Close" icon="close" round onClick={onClose} />
      </header>
      <div className="drawer__body">
        {isLoading && <Loading />}
        {error && (
          <ErrorState
            error={error}
            messages={{ 404: 'This subscription no longer exists.' }}
            onRetry={() => void refetch()}
          />
        )}
        {account && <DrawerBody account={account} />}
      </div>
      {account && (
        <footer className="drawer__foot">
          <Link
            className="btn btn--primary btn--small"
            to={`/subscriptions/${encodeURIComponent(account.id)}`}
            onClick={onClose}
          >
            Open record
          </Link>
        </footer>
      )}
    </>
  );
}

function DrawerBody({ account }: { account: SubscriptionDetail }) {
  return (
    <>
      <SubscriptionStats account={account} id="drawer-stats" />
      <MrrSpark account={account} ids={{ spark: 'drawer-spark' }} />
      <SubscriptionDetailsList items={subscriptionDetails(account)} id="drawer-details" />
      <SubscriptionHistory account={account} ids={{ events: 'drawer-events' }} />
    </>
  );
}
